import { User } from '../types'

export enum PermissionAction {
  Add = 'add',
  View = 'view',
  Change = 'change',
  Delete = 'delete',
  Display = 'display',
}

export enum PermissionType {
  Document = '%s_document',
  History = '%s_logentry',
  Note = '%s_note',
  ShareLink = '%s_sharelink',
  Correspondent = '%s_correspondent',
  Tag = '%s_tag',
  CustomField = '%s_customfield',
  DocumentType = '%s_documenttype',
  WorkflowStatus = '%s_workflowstatus',
  DocumentWorkflowDefinition = '%s_documentworkflowdefinition',
  DocumentWorkflowTask = '%s_documentworkflowtask',
  StoragePath = '%s_storagepath',
  SavedView = '%s_savedview',
  MailAccount = '%s_mailaccount',
  MailRule = '%s_mailrule',
  ProcessedMail = '%s_processedmail',
  Workflow = '%s_workflow',
  DocDotTask = '%s_docdottask',
  AppConfig = '%s_applicationconfiguration',
  UISettings = '%s_uisettings',
  User = '%s_user',
  Group = '%s_group',
  Organization = '%s_organization',
}

export interface ObjectWithPermissions {
  id?: number
  owner?: number | null
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  user_can_change?: boolean
}

class PermissionsService {
  private permissions: string[] = []
  private currentUser: User | null = null

  public initialize(permissions: string[], currentUser: User) {
    this.permissions = permissions
    this.currentUser = currentUser
  }

  public getCurrentUser(): User | null {
    return this.currentUser
  }

  public currentUserCan(action: PermissionAction, type: PermissionType): boolean {
    return this.currentUser?.is_superuser || this.permissions?.includes(this.getPermissionCode(action, type))
  }

  public isAdmin(): boolean {
    return this.currentUser?.is_staff || false
  }

  public isSuperUser(): boolean {
    return this.currentUser?.is_superuser || false
  }

  public currentUserOwnsObject(object: ObjectWithPermissions): boolean {
    return !object || !object.owner || this.currentUser?.is_superuser || object.owner === this.currentUser?.id
  }

  public currentUserHasObjectPermissions(action: string, object: ObjectWithPermissions): boolean {
    if (action === PermissionAction.View) {
      return (
        this.currentUserOwnsObject(object) ||
        object.permissions?.view.users.includes(this.currentUser?.id || 0) ||
        (object.permissions?.view.groups.filter((g) => this.currentUser?.groups?.includes(g)).length || 0) > 0
      )
    } else if (action === PermissionAction.Change) {
      return (
        this.currentUserOwnsObject(object) ||
        object.user_can_change ||
        object.permissions?.change.users.includes(this.currentUser?.id || 0) ||
        (object.permissions?.change.groups.filter((g) => this.currentUser?.groups?.includes(g)).length || 0) > 0
      )
    }
    return false
  }

  public getPermissionCode(action: PermissionAction, type: PermissionType): string {
    return type.replace('%s', action)
  }

  public getPermissionKeys(permissionStr: string): {
    actionKey: string | null
    typeKey: string | null
  } {
    for (const typeKey of Object.keys(PermissionType)) {
      const type = PermissionType[typeKey as keyof typeof PermissionType]
      for (const actionKey of Object.keys(PermissionAction)) {
        const action = PermissionAction[actionKey as keyof typeof PermissionAction]
        if (permissionStr === this.getPermissionCode(action, type)) {
          return { actionKey, typeKey }
        }
      }
    }
    return { actionKey: null, typeKey: null }
  }

  public getAllPermissionTypes(): string[] {
    return Object.keys(PermissionType)
  }

  public getAllPermissionActions(): string[] {
    return Object.keys(PermissionAction)
  }

  public getPermissionActionsForType(typeKey: string): string[] {
    if (typeKey === 'DocumentWorkflowTask') {
      return ['View', 'Display']
    }
    return Object.keys(PermissionAction)
  }
}

export const permissionsService = new PermissionsService()
